import React, { useState, useContext } from 'react'
import { StyleSheet, View } from 'react-native'
import {
  ModalComponent,
  useMobile,
  TextInput,
  Select,
  WfMockData,
  SelectOption,
  PulseOpsService,
  AppContext,
  AuthService,
  ErrorHandling,
  GeneralService,
  TaskType,
  Alert,
  sharedStyle,
  Checkbox,
  form2
} from '@pulseops/common'
import { useLoading } from '@mxt/zio-react'
import { useTranslation } from 'react-i18next'
import { pipe } from 'fp-ts/function'
import { ZIO } from '@mxt/zio'
import * as O from 'fp-ts/lib/Option'
import { SectionCol, SectionRow } from '@pulseops/submission/common'
import { Controller } from 'react-hook-form'
import * as t from 'io-ts'
import { withMessage } from 'io-ts-types'
import i18next from 'i18next'

const EndCaseFormCodec = t.intersection([
  t.type({
    isManualProcessing: t.union([t.boolean, t.undefined]),
    reason: withMessage(form2.selectOption.required, () => i18next.t('message:MS020001', { field: i18next.t('claim:Reason') })),

    note: t.union([t.string, t.undefined]),
  }),
  t.union([
    t.type({
      hasPolicyServiceCategory: t.literal(true),
      subReason: withMessage(form2.selectOption.required, () => i18next.t('message:MS020001', { field: i18next.t('Subreason') })),
    }),
    t.type({
      hasPolicyServiceCategory: t.literal(false),
      subReason: form2.selectOption.optional,
    })
  ])
])


export type EndPopupProps = {
  onClose: () => void
  onSuccess?: () => void
  open: boolean
  selectedValue: {
    taskId: string
    updateToCore: boolean
    category?: string
    caseId?: string
  }
}

export const EndPopup = (props: EndPopupProps) => {
  const { onClose, onSuccess, open, selectedValue } = props
  const { isWide } = useMobile()
  const [subreasonList, setSubreasonList] = useState<Array<SelectOption>>([])
  const [loading, bindLoader] = useLoading(false)
  const { showGlobalLoading, isGlobalLoadingVisible, showToast } = useContext(AppContext.AppContextInstance)
  const endCaseForm = form2.useForm(
    EndCaseFormCodec,
    {
      defaultValues: {
        isManualProcessing: false,
        reason: null,
        subReason: null,
        note: '',
        hasPolicyServiceCategory: selectedValue.category === TaskType.PolicyService
      }
    })

  const { t, i18n } = useTranslation()

  React.useEffect(() => {
    if (open) {
      endCaseForm.base.reset({
        isManualProcessing: false,
        reason: null,
        subReason: null,
        note: '',
        hasPolicyServiceCategory: selectedValue.category === TaskType.PolicyService
      })
    }
  }, [open])

  const { reasonOptions, originalReasonList } = React.useMemo(() => {
    if (open) {
      return pipe(
        selectedValue.category === TaskType.PolicyService
          ? WfMockData.PSEndedReasons
          : WfMockData.reasonEndWithCategory(selectedValue.category || ''),
        O.fromNullable,
        O.map((reasons) => {
          const reasonOptions = reasons.map((r) => ({
            label: i18n.language === 'vi' ? r.nameVi ?? r.name : r.name,
            value: r.code
          }))
          return {
            reasonOptions,
            originalReasonList: reasons
          }
        }),
        O.getOrElse(() => ({
          reasonOptions: [] as Array<SelectOption>,
          originalReasonList: [] as WfMockData.Reason[]
        })
        )
      )
    } else {
      return {
        reasonOptions: [],
        originalReasonList: []
      }
    }
  }, [open])

  const handleClose = () => {
    endCaseForm.base.reset({
      isManualProcessing: false,
      reason: null,
      subReason: null,
      note: '',
      hasPolicyServiceCategory: false
    })
    onClose()
  }

  const handleEndAction = () => {
    let reasonName: string = '', subReasonName: string = ''
    const isValidData = endCaseForm.base.formState.isValid
    if (isValidData) {
      showGlobalLoading(true)
      const formData = endCaseForm.base.getValues()
      if (selectedValue.category === TaskType.PolicyService) {
        reasonName = pipe(
          originalReasonList.find((item) => item.code === formData.reason?.value),
          O.fromNullable,
          O.fold(
            () => '',
            (reasonItem) => reasonItem.name
          )
        )
        subReasonName = pipe(
          WfMockData.PSEndedSubReasons.find((item) => item.code === formData.subReason?.value),
          O.fromNullable,
          O.fold(
            () => '',
            (reasonItem) => reasonItem.name
          )
        )
      } else {
        reasonName = formData.reason?.value || ''
        subReasonName = ''
      }
      pipe(
        AuthService.userInfo,
        ZIO.flatMap(({ email }) => {
          return PulseOpsService.endTask(
            selectedValue.taskId,
            {
              author: email,
              message: formData.note || '',
              reason: reasonName,
              subReasonCode: subReasonName,
              isManualProcessing: formData.isManualProcessing || false
            },
            selectedValue.updateToCore,
            selectedValue.category
          )
        }),
        ZIO.tap(() => {
          if (selectedValue.category === TaskType.Claim) {
            pipe(GeneralService.triggerActionClaim(selectedValue.caseId || ''), ZIO.unsafeRun({}))
          }
          showGlobalLoading(false)
          handleClose()
          showToast(t('requestInfo:EndSuccess'), 'success')
          onSuccess && onSuccess()
          return ZIO.unit
        }),
        ZIO.catchAll((_error: any) => {
          const status = _error?.source?.response?.status
          const errorMsg = pipe(
            i18n.language === 'en' ? _error?.source.response.data?.message : _error?.source.response.data?.messageVN,
            O.fromNullable,
            O.fold(
              () => '',
              (result) => result
            )
          )
          handleClose()
          showGlobalLoading(false)
          if (status === 401) {
            return Alert.snackbarM(errorMsg, '', 'error')
          } else {
            return Alert.snackbarM(t('requestInfo:EndFailed'), '', 'error')
          }
        }),
        bindLoader,
        ZIO.unsafeRun({})
      )
    }
  }

  const onChangePCEndedReason = (selectedOption: SelectOption | null) => {
    setSubreasonList([])
    endCaseForm.base.setValue('subReason', null, { shouldValidate: true })
    if (!!selectedOption) {
      const subList = pipe(
        O.fromNullable(WfMockData.PSEndedSubReasons.filter((subReason) => subReason.parentCode === selectedOption.value)),
        O.map((subItemList) => {
          return subItemList.map((item) => ({ label: i18n.language === 'en' ? (item.name || '') : (item.nameVi || ''), value: item.code }))
        }),
        O.getOrElse(() => [] as Array<SelectOption>),
      )
      setSubreasonList(subList)
    }
  }

  return (
    <ModalComponent
      title={t('requestInfo:End')}
      visible={open && !isGlobalLoadingVisible}
      onClose={handleClose}
      modalWidth={700}
      actions={[
        {
          text: t('common:Cancel'),
          type: 'outline',
          disabled: false,
          loading: false,
          onlyWide: false,
          style: { height: 39, marginEnd: 15 },
          action: handleClose
        },
        {
          text: t('requestInfo:End'),
          type: 'filled',
          disabled: !endCaseForm.base.formState.isValid,
          loading,
          onlyWide: false,
          style: { height: 39 },
          action: handleEndAction
        }
      ]}
    >
      <View
        style={{
          marginHorizontal: 20,
          marginTop: isWide ? 20 : 16
        }}
      >
        {selectedValue.category === TaskType.PolicyService && (
          <SectionRow>
            <SectionCol sectionStyles={sharedStyle.col_6}>
              <Controller
                control={endCaseForm.base.control}
                name="isManualProcessing"
                render={({ field: { onChange, value, onBlur } }) => (
                  <Checkbox
                    title={t('TaskManagement:ManualProcessingOnLAS')}
                    value={value}
                    onChange={(val) => {
                      onChange(val)
                    }}
                    onBlur={onBlur}
                    checkBoxStyle={endStyles.checkboxContent}
                    textStyle={{ fontWeight: 'bold' }}
                  />
                )}
              />
            </SectionCol>
          </SectionRow>
        )}
        <SectionRow sectionStyles={[selectedValue.category === TaskType.PolicyService && endStyles.secondLine]}>
          <SectionCol sectionStyles={sharedStyle.col_6}>
            <Controller
              control={endCaseForm.base.control}
              name="reason"
              render={({ field: { onChange, value, onBlur } }) => (
                <Select
                  value={value}
                  onChange={(val) => {
                    onChange(val)
                    onChangePCEndedReason(val)
                  }}
                  label={t('claim:Reason')}
                  required={true}
                  options={reasonOptions}
                />
              )}
            />
          </SectionCol>
          {selectedValue.category === TaskType.PolicyService && (
            <SectionCol sectionStyles={sharedStyle.col_6}>
              <Controller
                control={endCaseForm.base.control}
                name="subReason"
                render={({ field: { onChange, value, onBlur } }) => (
                  <Select
                    value={value}
                    onChange={onChange}
                    label={t('TaskManagement:Subreason')}
                    required={true}
                    options={subreasonList}
                  />
                )}
              />
            </SectionCol>
          )}
        </SectionRow>
        <SectionRow sectionStyles={endStyles.secondLine}>
          <SectionCol sectionStyles={sharedStyle.col_12}>
            <Controller
              control={endCaseForm.base.control}
              name="note"
              render={({ field: { onChange, value, onBlur } }) => (
                <TextInput
                  title={t('TaskManagement:Comment')}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  placeholder={t('TaskManagement:WriteComment')}
                  textInputProps={{
                    maxLength: 500,
                    multiline: true,
                    style: [
                      endStyles.textArea,
                      { marginTop: 10, height: isWide ? 140 : 60 }
                    ],
                    placeholderTextColor: '#B0B0B0'
                  }}
                />
              )}
            />
          </SectionCol>
        </SectionRow>
      </View>
    </ModalComponent>
  )
}
const endStyles = StyleSheet.create({
  checkboxContent: {
    padding: 0,
    marginTop: 0,
    paddingLeft: 0
  },
  textArea: {
    borderWidth: 1,
    borderColor: '#D3DCE6',
    borderRadius: 8,
    padding: 16,
    fontSize: 15,
    lineHeight: 22,
    color: '#000000'
  },
  secondLine: {
    marginTop: 10
  }
})
